import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CreditCard, DollarSign, Building2, Loader2, Store, AlertTriangle, Copy, Check } from 'lucide-react';
import axiosInstance from '../api/axios';
import useCartStore from '../store/cartStore';
import useAuthStore from '../store/authStore';
import { toast } from 'sonner';
import { formatEUR } from '../utils/format';

const Checkout = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { items, getTotal, clearCart } = useCartStore();
  const { isAuthenticated, user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [loadingMethods, setLoadingMethods] = useState(true);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState('');
  const [paypalClientId, setPaypalClientId] = useState('');
  const [paypalMode, setPaypalMode] = useState('sandbox');
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [validatingCode, setValidatingCode] = useState(false);
  const [discountError, setDiscountError] = useState('');
  const [isGuest, setIsGuest] = useState(!isAuthenticated);
  const [guestEmail, setGuestEmail] = useState('');
  const [shippingCost, setShippingCost] = useState(0);
  const [isFreeShipping, setIsFreeShipping] = useState(false);
  const [remainingForFree, setRemainingForFree] = useState(0);
  const [shippingData, setShippingData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    street: '',
    house_number: '',
    postal_code: '',
    city: '',
    country: 'Germany',
  });
  
  // Multi-vendor state
  const [vendorInfo, setVendorInfo] = useState(null);
  const [isMultiVendor, setIsMultiVendor] = useState(false);
  const [copiedIban, setCopiedIban] = useState(null);

  // Hydration tracker for zustand persist (items hydrate async from localStorage)
  const [hydrated, setHydrated] = useState(() => useCartStore.persist?.hasHydrated?.() ?? true);
  useEffect(() => {
    if (hydrated) return;
    const unsub = useCartStore.persist?.onFinishHydration?.(() => setHydrated(true));
    // Fallback: in case persist is unavailable, mark hydrated after one tick
    const t = setTimeout(() => setHydrated(true), 500);
    return () => { if (typeof unsub === 'function') unsub(); clearTimeout(t); };
  }, [hydrated]);

  // Run data fetches once on mount (regardless of cart state) so the form is
  // ready when the user arrives — even if zustand-persist hasn't fully hydrated yet.
  useEffect(() => {
    fetchPaymentMethods();
    fetchVendorPaymentInfo();
  }, []);

  // Wenn Käufer von Stripe mit ?cancelled=1&order_id=... zurückkommt:
  // markiere die Order als abgebrochen und zeige einen Hinweis.
  const [cancelBanner, setCancelBanner] = useState(null); // {orderNumber} oder null
  useEffect(() => {
    const cancelled = searchParams.get('cancelled');
    const orderId = searchParams.get('order_id');
    if (!cancelled || !orderId) return;
    (async () => {
      try {
        await axiosInstance.post('/stripe/orders/cancelled', { order_id: orderId });
        setCancelBanner({ orderId });
        toast.error('Zahlung wurde abgebrochen. Bitte versuche eine andere Zahlungsart.', { duration: 6000 });
      } catch (e) {
        // still show banner
        setCancelBanner({ orderId });
      }
    })();
    // eslint-disable-next-line
  }, []);

  const orderPlacedRef = useRef(false);
  // Redirect to /cart if cart is empty AFTER hydration completes.
  useEffect(() => {
    if (!hydrated || orderPlacedRef.current) return;
    if (items.length === 0) {
      navigate('/cart');
      return;
    }
    calculateShipping();
  }, [items, hydrated]);

  const calculateShipping = async () => {
    try {
      const productIds = items.map(item => item.product_id);
      const subtotal = getTotal();
      
      const response = await axiosInstance.post('/shipping/calculate', {
        product_ids: productIds,
        subtotal: subtotal
      });
      
      setShippingCost(response.data.shipping_cost);
      setIsFreeShipping(response.data.is_free_shipping);
      setRemainingForFree(response.data.remaining_for_free_shipping || 0);
    } catch (error) {
      console.error('Error calculating shipping:', error);
      // Default shipping
      setShippingCost(3.99);
    }
  };

  const fetchVendorPaymentInfo = async () => {
    try {
      const productIds = items.map(item => item.product_id);
      const response = await axiosInstance.post('/vendors/payment-info', productIds);
      setVendorInfo(response.data);
      setIsMultiVendor(response.data.is_multi_vendor || response.data.vendors?.length > 0);
    } catch (error) {
      console.error('Error fetching vendor info:', error);
    }
  };

  const copyToClipboard = (text, vendorId) => {
    navigator.clipboard.writeText(text);
    setCopiedIban(vendorId);
    toast.success('IBAN kopiert!');
    setTimeout(() => setCopiedIban(null), 2000);
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await axiosInstance.get('/payment-methods');
      setPaymentMethods(response.data.methods || []);
      
      // Get PayPal config
      const paypalMethod = response.data.methods?.find(m => m.id === 'paypal');
      if (paypalMethod) {
        setPaypalClientId(paypalMethod.client_id || '');
        setPaypalMode(paypalMethod.mode || 'sandbox');
      }
      
      // Set first method as default
      if (response.data.methods && response.data.methods.length > 0) {
        setSelectedMethod(response.data.methods[0].id);
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      toast.error('Fehler beim Laden der Zahlungsmethoden');
    } finally {
      setLoadingMethods(false);
    }
  };

  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) {
      setDiscountError('Bitte einen Code eingeben');
      return;
    }
    setDiscountError('');
    setValidatingCode(true);
    try {
      const orderValue = items.reduce((sum, it) => sum + (it.price * it.quantity), 0);
      const response = await axiosInstance.post('/coupons/validate', {
        code: discountCode.trim(),
        order_value: orderValue,
      });
      const c = response.data?.coupon || {};
      setAppliedDiscount({
        code: c.code || discountCode.trim().toUpperCase(),
        discount_percentage: Number(c.discount_value || 0),
        discount_type: c.discount_type || 'percentage',
        discount_amount: response.data?.discount_amount || 0,
        min_order_value: Number(c.min_order_value || 0),
        description: c.description || '',
      });
      toast.success(`Rabatt „${c.code}" angewendet — du sparst ${formatEUR(Number(response.data?.discount_amount || 0))}`);
    } catch (error) {
      setDiscountError(error.response?.data?.detail || 'Ungültiger Rabattcode');
    } finally {
      setValidatingCode(false);
    }
  };

  const handleRemoveDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode('');
    setDiscountError('');
    toast.info('Rabatt entfernt');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate guest email if not authenticated
    if (!isAuthenticated && !guestEmail) {
      toast.error('Bitte geben Sie Ihre E-Mail-Adresse ein');
      return;
    }
    
    setLoading(true);

    try {
      const orderItems = items.map(item => ({
        product_id: item.product_id,
        product_name: item.product?.name || 'Produkt',
        quantity: item.quantity,
        price: item.price,
        selected_color: item.selected_color || null,
        gift_label_text: item.gift_label_text || null,
        gift_note: item.gift_note || null,
        delivery_time: item.product?.delivery_time || '1-3',
      }));

      // Apply discount if present — server already increments uses_count on
      // order creation; the explicit /coupons/apply call is no longer needed.

      // Handle different payment methods
      if (selectedMethod === 'stripe') {
        // First create the order
        const orderResponse = await axiosInstance.post('/orders', {
          items: orderItems,
          payment_method: 'stripe',
          shipping_address: shippingData,
          guest_email: !isAuthenticated ? guestEmail : null,
          is_guest: !isAuthenticated,
          discount_code: appliedDiscount?.code || null,
          shipping_cost: shippingCost
        });

        const orderId = orderResponse.data.id;

        // Then create Stripe checkout session
        const origin = window.location.origin;
        const stripeResponse = await axiosInstance.post('/stripe/create-checkout-session', {
          order_id: orderId,
          origin_url: origin
        });

        // Redirect to Stripe
        window.location.href = stripeResponse.data.checkout_url;
        return;
        
      } else if (selectedMethod === 'paypal') {
        // PayPal - handled by PayPal buttons below
        toast.info('Bitte nutzen Sie den PayPal-Button unten');
        setLoading(false);
        return;
        
      } else if (selectedMethod === 'bank_transfer') {
        // Bank transfer - create order directly
        const response = await axiosInstance.post('/orders', {
          items: orderItems,
          payment_method: 'bank_transfer',
          shipping_address: shippingData,
          guest_email: !isAuthenticated ? guestEmail : null,
          is_guest: !isAuthenticated,
          discount_code: appliedDiscount?.code || null,
          shipping_cost: shippingCost
        });

        orderPlacedRef.current = true;
        clearCart();
        toast.success('Bestellung erfolgreich aufgegeben!');
        navigate(`/checkout/bank-transfer-success?order_id=${response.data.id}`);
        return;
        
      } else {
        // Other methods (PayPal will be handled separately with PayPal buttons)
        toast.error('Diese Zahlungsmethode wird noch implementiert');
      }

    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Checkout');
    } finally {
      setLoading(false);
    }
  };

  // Use calculated shipping cost from state
  const subtotal = getTotal();
  // Rabatt-Berechnung: Prozent auf aktuellen Zwischensumme, Festbetrag direkt.
  // Nie mehr als der Zwischensumme abziehen.
  const discountAmount = (() => {
    if (!appliedDiscount) return 0;
    if (appliedDiscount.discount_type === 'fixed') {
      return Math.min(subtotal, Number(appliedDiscount.discount_amount || 0));
    }
    return subtotal * (Number(appliedDiscount.discount_percentage || 0) / 100);
  })();
  const subtotalAfterDiscount = Math.max(0, subtotal - discountAmount);
  const total = subtotalAfterDiscount + shippingCost;

  const getPaymentIcon = (methodId) => {
    switch (methodId) {
      case 'stripe':
        return <CreditCard className="h-5 w-5" />;
      case 'paypal':
        return <DollarSign className="h-5 w-5" />;
      case 'bank_transfer':
        return <Building2 className="h-5 w-5" />;
      default:
        return <CreditCard className="h-5 w-5" />;
    }
  };

  if (items.length === 0) {
    return null;
  }

  if (loadingMethods) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" style={{ backgroundColor: '#E8E0D5' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Kasse</h1>

        {cancelBanner && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6" data-testid="payment-cancel-banner">
            <p className="font-semibold text-red-900 mb-1">Zahlung nicht abgeschlossen</p>
            <p className="text-sm text-red-800">
              Die Zahlung wurde abgebrochen oder konnte nicht verarbeitet werden.
              Bitte wähle eine andere Zahlungsart (z.B. Banküberweisung) oder versuche es erneut.
              Dein Warenkorb ist erhalten geblieben.
            </p>
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Checkout Form */}
            <div className="lg:col-span-2 space-y-6">
              {/* Shipping Address */}
              <Card>
                <CardHeader>
                  <CardTitle>Lieferadresse</CardTitle>
                  {!isAuthenticated && (
                    <p className="text-sm text-gray-600 mt-2">
                      Sie kaufen als Gast. Möchten Sie ein Konto erstellen? 
                      <a href="/register" className="text-[#C4A084] hover:underline ml-1">Hier registrieren</a>
                    </p>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  {!isAuthenticated && (
                    <div>
                      <Label htmlFor="guest_email">E-Mail-Adresse *</Label>
                      <Input
                        id="guest_email"
                        type="email"
                        required
                        placeholder="ihre@email.de"
                        value={guestEmail}
                        onChange={(e) => setGuestEmail(e.target.value)}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Für Bestellbestätigung und Tracking
                      </p>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="first_name">Vorname *</Label>
                      <Input
                        id="first_name"
                        required
                        value={shippingData.first_name}
                        onChange={(e) => setShippingData({ ...shippingData, first_name: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="last_name">Nachname *</Label>
                      <Input
                        id="last_name"
                        required
                        value={shippingData.last_name}
                        onChange={(e) => setShippingData({ ...shippingData, last_name: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-2">
                      <Label htmlFor="street">Straße *</Label>
                      <Input
                        id="street"
                        required
                        value={shippingData.street}
                        onChange={(e) => setShippingData({ ...shippingData, street: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="house_number">Hausnr. *</Label>
                      <Input
                        id="house_number"
                        required
                        value={shippingData.house_number}
                        onChange={(e) => setShippingData({ ...shippingData, house_number: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="postal_code">PLZ *</Label>
                      <Input
                        id="postal_code"
                        required
                        value={shippingData.postal_code}
                        onChange={(e) => setShippingData({ ...shippingData, postal_code: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="city">Stadt *</Label>
                      <Input
                        id="city"
                        required
                        value={shippingData.city}
                        onChange={(e) => setShippingData({ ...shippingData, city: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-2 text-xs text-[#6D5A47] bg-[#FAF5EE] border border-[#E8DCC8] px-3 py-2">
                    <span>🇩🇪</span>
                    <span>Wir versenden derzeit ausschließlich innerhalb Deutschlands.</span>
                  </div>
                </CardContent>
              </Card>

              {/* Payment Method */}
              <Card>
                <CardHeader>
                  <CardTitle>Zahlungsmethode</CardTitle>
                </CardHeader>
                <CardContent>
                  {paymentMethods.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-gray-600">Keine Zahlungsmethoden verfügbar</p>
                      <p className="text-sm text-gray-500 mt-2">
                        Bitte kontaktieren Sie den Administrator
                      </p>
                    </div>
                  ) : (
                    <RadioGroup value={selectedMethod} onValueChange={setSelectedMethod}>
                      {paymentMethods.map((method) => (
                        <div 
                          key={method.id}
                          className="flex items-center space-x-3 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer"
                        >
                          <RadioGroupItem value={method.id} id={method.id} />
                          <Label htmlFor={method.id} className="flex-1 cursor-pointer">
                            <div className="flex items-center gap-3">
                              {getPaymentIcon(method.id)}
                              <div>
                                <div className="font-medium">{method.name}</div>
                                <div className="text-sm text-gray-600">{method.description}</div>
                              </div>
                            </div>
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                  )}

                  {/* Bank Transfer - Hybrid: nur Hinweis, keine Daten */}
                  {selectedMethod === 'bank_transfer' && (
                    <div className="mt-4 p-5 bg-blue-50 border-l-4 border-blue-400">
                      <h4 className="font-semibold mb-3 text-blue-900 text-base">💙 Zahlung per Banküberweisung</h4>
                      <ul className="text-sm text-blue-900 space-y-2 leading-relaxed list-disc pl-5">
                        <li>Sie erhalten <strong>unmittelbar nach Bestellabschluss</strong> eine E-Mail mit unseren Bankdaten und Ihrer Bestellnummer als Verwendungszweck.</li>
                        <li>Bitte überweisen Sie den Rechnungsbetrag <strong>innerhalb von 7 Tagen</strong> ab Bestelldatum.</li>
                        <li>Geben Sie unbedingt <strong>Ihre Bestellnummer</strong> als Verwendungszweck an, damit wir Ihre Zahlung zuordnen können.</li>
                        <li>Sobald Ihre Zahlung bei uns eingegangen ist, wird Ihre Bestellung <strong>umgehend bearbeitet und versendet</strong>.</li>
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <Card className="sticky top-24">
                <CardHeader>
                  <CardTitle>Bestellung</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Multi-Vendor Notice */}
                  {isMultiVendor && vendorInfo?.vendors?.length > 0 && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-orange-800">
                            Mehrere Verkäufer in dieser Bestellung
                          </p>
                          <p className="text-xs text-orange-700 mt-1">
                            Sie zahlen direkt an jeden Verkäufer. Die Zahlungsdaten finden Sie unten.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Order Items - Grouped by Vendor */}
                  {vendorInfo?.vendors?.length > 0 ? (
                    <div className="space-y-6">
                      {vendorInfo.vendors.map((vendor) => (
                        <div key={vendor.vendor_id} className="border rounded-lg overflow-hidden">
                          {/* Vendor Header */}
                          <div className="bg-gray-50 px-4 py-3 border-b">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Store className="h-4 w-4 text-gray-600" />
                                <span className="font-medium text-gray-800">{vendor.shop_name}</span>
                              </div>
                              <Badge variant="outline" className="text-xs">
                                {vendor.products.length} Artikel
                              </Badge>
                            </div>
                          </div>
                          
                          {/* Vendor Products */}
                          <div className="p-4 space-y-2">
                            {vendor.products.map((product) => {
                              const cartItem = items.find(i => i.product_id === product.id);
                              const qty = cartItem?.quantity || 1;
                              return (
                                <div key={product.id} className="flex justify-between text-sm">
                                  <div className="flex-1">
                                    <span className="text-gray-800">{qty}x {product.name}</span>
                                    {product.sku && (
                                      <span className="text-xs text-gray-500 ml-2">({product.sku})</span>
                                    )}
                                  </div>
                                  <span className="font-medium">{formatEUR((product.price * qty))}</span>
                                </div>
                              );
                            })}
                            <div className="pt-2 mt-2 border-t flex justify-between font-medium">
                              <span>Zwischensumme {vendor.shop_name}:</span>
                              <span>{formatEUR(vendor.subtotal)}</span>
                            </div>
                          </div>
                          
                          {/* Vendor Payment Info */}
                          <div className="bg-blue-50 px-4 py-3 border-t">
                            <p className="text-xs font-medium text-blue-800 mb-3">
                              💳 Zahlungsoptionen für {vendor.shop_name}:
                            </p>
                            
                            <div className="space-y-2">
                              {/* PayPal.me Link */}
                              {vendor.payment_details?.paypal_me_link && (
                                <a 
                                  href={`${vendor.payment_details.paypal_me_link}/${vendor.subtotal.toFixed(2)}EUR`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-2 bg-[#0070ba] text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-[#003087] transition-colors"
                                >
                                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
                                    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106z"/>
                                  </svg>
                                  Mit PayPal bezahlen ({formatEUR(vendor.subtotal)})
                                </a>
                              )}
                              
                              {/* PayPal Email (fallback) */}
                              {!vendor.payment_details?.paypal_me_link && vendor.payment_details?.paypal_email && (
                                <div className="text-xs text-blue-700 bg-white p-2 rounded border">
                                  <span className="font-medium">PayPal:</span> {vendor.payment_details.paypal_email}
                                </div>
                              )}
                              
                              {/* Stripe Payment Link */}
                              {vendor.payment_details?.stripe_payment_link && (
                                <a 
                                  href={vendor.payment_details.stripe_payment_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-2 bg-[#635BFF] text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-[#4B45C6] transition-colors"
                                >
                                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
                                    <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
                                  </svg>
                                  Mit Stripe bezahlen
                                </a>
                              )}
                              
                              {/* Klarna Link */}
                              {vendor.payment_details?.klarna_link && (
                                <a 
                                  href={vendor.payment_details.klarna_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-2 bg-[#FFB3C7] text-black px-3 py-2 rounded-lg text-sm font-medium hover:bg-[#FFA0B8] transition-colors"
                                >
                                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
                                    <path d="M4.592 2v20H0V2h4.592zm10.456 0a8.04 8.04 0 0 1-3.166 6.396L8.938 11.39l5.128 6.748v3.862h-4.9l-4.86-6.393.002-.002a12.042 12.042 0 0 0 4.63-9.605H4.593V2h10.455zM24 18.857a3.143 3.143 0 1 1-6.286 0 3.143 3.143 0 0 1 6.286 0z"/>
                                  </svg>
                                  Mit Klarna bezahlen
                                </a>
                              )}
                              
                              {/* Bank Transfer */}
                              {vendor.payment_details?.bank_account && (
                                <div className="bg-white p-3 rounded border text-xs space-y-1">
                                  <p className="font-medium text-gray-700 mb-2">🏦 Banküberweisung:</p>
                                  <div className="text-gray-600">
                                    <div><span className="font-medium">Empfänger:</span> {vendor.payment_details.bank_account.account_holder}</div>
                                    <div className="flex items-center gap-1">
                                      <span className="font-medium">IBAN:</span> 
                                      <span className="font-mono">{vendor.payment_details.bank_account.iban}</span>
                                      <button
                                        type="button"
                                        onClick={() => copyToClipboard(vendor.payment_details.bank_account.iban, vendor.vendor_id)}
                                        className="ml-1 text-blue-600 hover:text-blue-800"
                                      >
                                        {copiedIban === vendor.vendor_id ? (
                                          <Check className="h-3 w-3" />
                                        ) : (
                                          <Copy className="h-3 w-3" />
                                        )}
                                      </button>
                                    </div>
                                    {vendor.payment_details.bank_account.bic && (
                                      <div><span className="font-medium">BIC:</span> {vendor.payment_details.bank_account.bic}</div>
                                    )}
                                    <div className="mt-2 text-gray-500">
                                      Betrag: <span className="font-medium text-gray-800">{formatEUR(vendor.subtotal)}</span>
                                    </div>
                                  </div>
                                </div>
                              )}
                              
                              {/* Custom Instructions */}
                              {vendor.payment_details?.custom_payment_instructions && (
                                <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded border-l-2 border-blue-400">
                                  <p className="font-medium mb-1">Hinweis vom Verkäufer:</p>
                                  <p>{vendor.payment_details.custom_payment_instructions}</p>
                                </div>
                              )}
                              
                              {/* No payment methods */}
                              {!vendor.payment_details?.paypal_me_link && 
                               !vendor.payment_details?.paypal_email && 
                               !vendor.payment_details?.stripe_payment_link &&
                               !vendor.payment_details?.klarna_link &&
                               !vendor.payment_details?.bank_account && (
                                <p className="text-xs text-orange-600 italic bg-orange-50 p-2 rounded">
                                  ⚠️ Verkäufer hat noch keine Zahlungsdaten hinterlegt. Bitte kontaktieren Sie den Verkäufer.
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {/* Admin Products (if any) */}
                      {vendorInfo.has_admin_products && vendorInfo.admin_products?.length > 0 && (
                        <div className="border rounded-lg overflow-hidden">
                          <div className="bg-purple-50 px-4 py-3 border-b">
                            <div className="flex items-center gap-2">
                              <Store className="h-4 w-4 text-purple-600" />
                              <span className="font-medium text-purple-800">Shop-eigene Produkte</span>
                            </div>
                          </div>
                          <div className="p-4 space-y-2">
                            {vendorInfo.admin_products.map((product) => {
                              const cartItem = items.find(i => i.product_id === product.id);
                              const qty = cartItem?.quantity || 1;
                              return (
                                <div key={product.id} className="flex justify-between text-sm">
                                  <span className="text-gray-800">{qty}x {product.name}</span>
                                  <span className="font-medium">{formatEUR((product.price * qty))}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Regular items display (no vendor info) */
                    <div className="space-y-3">
                      {items.map((item) => (
                        <div key={`${item.product_id}-${item.selected_color || 'default'}`} className="flex justify-between text-sm border-b border-gray-100 pb-2">
                          <div className="flex-1">
                            <div className="text-gray-800 font-medium">
                              {item.quantity}x {item.product?.name}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              {item.product?.sku && <span className="mr-2">Art.-Nr.: {item.product.sku}</span>}
                              {item.selected_color && <span>Farbe: {item.selected_color}</span>}
                            </div>
                          </div>
                          <span className="font-medium text-gray-800">{formatEUR((item.price * item.quantity))}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Discount Code */}
                  <div className="pt-4 border-t">
                    <Label htmlFor="discount">Rabattcode</Label>
                    {!appliedDiscount ? (
                      <>
                        <div className="flex gap-2 mt-2">
                          <Input
                            id="discount"
                            value={discountCode}
                            onChange={(e) => {
                              setDiscountCode(e.target.value.toUpperCase());
                              if (discountError) setDiscountError('');
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleApplyDiscount();
                              }
                            }}
                            placeholder="z. B. WILLKOMMEN10"
                            className="uppercase tracking-wider"
                            data-testid="checkout-discount-input"
                          />
                          <Button
                            type="button"
                            onClick={handleApplyDiscount}
                            disabled={validatingCode || !discountCode.trim()}
                            className="rounded-none text-white border-none px-5"
                            style={{ backgroundColor: '#5D4E3C' }}
                            data-testid="checkout-discount-apply"
                          >
                            {validatingCode ? 'Prüft…' : 'Anwenden'}
                          </Button>
                        </div>
                        {discountError && (
                          <p className="text-sm text-[#B0512E] mt-2 flex items-start gap-1" data-testid="checkout-discount-error">
                            <span className="mt-0.5">⚠</span>
                            <span>{discountError}</span>
                          </p>
                        )}
                        <p className="text-xs text-gray-500 mt-1.5">
                          Nur ein Rabattcode pro Bestellung möglich.
                        </p>
                      </>
                    ) : (
                      <div
                        className="mt-2 border border-[#B4DDD0] bg-[#F0F9F4] px-3 py-3 flex items-start justify-between gap-3"
                        data-testid="checkout-discount-applied"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <code className="inline-block px-2 py-0.5 bg-white border border-[#B4DDD0] text-[#2F6B4F] font-bold text-sm tracking-wider">
                              {appliedDiscount.code}
                            </code>
                            <span className="text-sm font-semibold text-[#2F6B4F]">
                              {appliedDiscount.discount_type === 'fixed'
                                ? `−${formatEUR(Number(appliedDiscount.discount_amount || 0))}`
                                : `−${appliedDiscount.discount_percentage}%`}
                            </span>
                          </div>
                          <p className="text-[13px] text-[#2F6B4F] mt-1">
                            Du sparst <strong>{formatEUR(discountAmount)}</strong>
                          </p>
                          {appliedDiscount.description && (
                            <p className="text-[11px] text-[#5A7A6A] mt-0.5 truncate" title={appliedDiscount.description}>
                              {appliedDiscount.description}
                            </p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={handleRemoveDiscount}
                          className="text-[#8B7355] hover:text-[#B0512E] text-xs underline shrink-0"
                          data-testid="checkout-discount-remove"
                        >
                          Entfernen
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Totals */}
                  <div className="space-y-2 pt-4 border-t">
                    <div className="flex justify-between text-sm">
                      <span>Zwischensumme</span>
                      <span className={appliedDiscount ? 'text-gray-500 line-through' : ''}>
                        {formatEUR(subtotal)}
                      </span>
                    </div>
                    {appliedDiscount && (
                      <>
                        <div className="flex justify-between text-sm text-[#2F6B4F]">
                          <span>Rabatt ({appliedDiscount.discount_type === 'fixed'
                            ? `${formatEUR(Number(appliedDiscount.discount_amount || 0))}`
                            : `${appliedDiscount.discount_percentage}%`})</span>
                          <span>−{formatEUR(discountAmount)}</span>
                        </div>
                        <div className="flex justify-between text-sm font-semibold">
                          <span>Zwischensumme nach Rabatt</span>
                          <span>{formatEUR(subtotalAfterDiscount)}</span>
                        </div>
                      </>
                    )}
                    <div className="flex justify-between text-sm">
                      <span>Versand</span>
                      <span className={shippingCost === 0 ? 'text-green-600 font-medium' : ''}>
                        {shippingCost === 0 ? 'Kostenlos ✓' : `${formatEUR(shippingCost)}`}
                      </span>
                    </div>
                    {shippingCost > 0 && remainingForFree > 0 && (
                      <div className="text-xs text-gray-500 -mt-1">
                        Noch {formatEUR(remainingForFree)} bis kostenloser Versand
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-lg pt-2 border-t">
                      <span>Gesamt</span>
                      <span data-testid="checkout-total">{formatEUR(total)}</span>
                    </div>
                    {appliedDiscount && (
                      <div className="text-[12px] text-[#2F6B4F] text-right">
                        Du hast <strong>{formatEUR(discountAmount)}</strong> gespart 🎉
                      </div>
                    )}
                  </div>

                  {/* PayPal Buttons */}
                  {selectedMethod === 'paypal' && paypalClientId ? (
                    <div className="w-full space-y-3">
                      <Button
                        type="button"
                        className="w-full bg-[#0070ba] hover:bg-[#003087] text-white font-semibold py-3"
                        disabled={loading}
                        onClick={async () => {
                          setLoading(true);
                          try {
                            // Validate form first
                            if (!isAuthenticated && !guestEmail) {
                              toast.error('Bitte geben Sie Ihre E-Mail-Adresse ein');
                              setLoading(false);
                              return;
                            }
                            if (!shippingData.first_name || !shippingData.last_name || !shippingData.street || !shippingData.postal_code || !shippingData.city) {
                              toast.error('Bitte füllen Sie alle Pflichtfelder aus');
                              setLoading(false);
                              return;
                            }

                            const orderItems = items.map(item => ({
                              product_id: item.product_id,
                              product_name: item.product?.name || 'Produkt',
                              quantity: item.quantity,
                              price: item.price,
                              selected_color: item.selected_color || null,
                              gift_label_text: item.gift_label_text || null,
                              gift_note: item.gift_note || null,
                              delivery_time: item.product?.delivery_time || '1-3',
                            }));

                            // Create PayPal order
                            const response = await axiosInstance.post('/paypal/create-order', {
                              amount: total,
                              currency: 'EUR',
                              order_items: JSON.stringify(orderItems),
                              shipping_address: JSON.stringify({
                                ...shippingData,
                                email: guestEmail || user?.email
                              })
                            });

                            if (response.data.transaction_id) {
                              // Open PayPal in a new window for approval
                              const paypalUrl = paypalMode === 'live' 
                                ? `https://www.paypal.com/checkoutnow?token=${response.data.transaction_id}`
                                : `https://www.sandbox.paypal.com/checkoutnow?token=${response.data.transaction_id}`;
                              
                              window.location.href = paypalUrl;
                            }
                          } catch (error) {
                            console.error('PayPal error:', error);
                            toast.error('Fehler beim Erstellen der PayPal-Bestellung');
                          } finally {
                            setLoading(false);
                          }
                        }}
                      >
                        {loading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Wird verarbeitet...
                          </>
                        ) : (
                          <>
                            <svg viewBox="0 0 24 24" className="h-5 w-5 mr-2 inline" fill="currentColor">
                              <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106z"/>
                            </svg>
                            Mit PayPal bezahlen
                          </>
                        )}
                      </Button>
                      <p className="text-xs text-gray-500 text-center">
                        Sie werden zu PayPal weitergeleitet, um die Zahlung abzuschließen
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Submit Button for other methods */}
                      <p className="text-xs text-gray-600 leading-relaxed" data-testid="checkout-legal-notice">
                        Mit deiner Bestellung erklärst du dich mit unseren{' '}
                        <a href="/agb" target="_blank" rel="noopener noreferrer" className="underline">AGB</a> einverstanden.
                        Bitte beachte unsere{' '}
                        <a href="/widerruf" target="_blank" rel="noopener noreferrer" className="underline">Widerrufsbelehrung</a> und{' '}
                        <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="underline">Datenschutzerklärung</a>.
                      </p>
                      <Button 
                        type="submit" 
                        className="w-full bg-gradient-to-r from-[#C4A084] to-[#B89968] text-white"
                        disabled={loading || paymentMethods.length === 0}
                        data-testid="checkout-submit-button"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Verarbeite...
                          </>
                        ) : (
                          'Zahlungspflichtig bestellen'
                        )}
                      </Button>

                      <p className="text-xs text-gray-500 text-center">
                        Sichere Zahlung über {paymentMethods.find(m => m.id === selectedMethod)?.name}
                      </p>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Checkout;
